import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationIcon, NotificationIconUnread } from './icons/Icons';
import { markAllAsRead, setNotifications } from '../slicers/NotificationSlice';
import useAxios from '../services/AxiosConfig';
import image from "../assets/NoBellNotification.png";
import './BellNotification.css';
import AllNotificationsPopup from './AllNotificationsPopup';
import bloodPressure from '../assets/Property 1=Blood pressure.png';
import notes from '../assets/Property 1=Notes.png'
import device from '../assets/Property 1=Patient Vitals.png'
import { CloseButton } from 'react-bootstrap';

const BellNotification = () => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUnread, setShowUnread] = useState(true);
  const notifications = useSelector((state) => state.notificationsData.notifications);
  const hasUnread = useSelector((state) => state.notificationsData.hasUnread);
  const axiosConfig = useSelector(state => state.axiosConfig);
  const userId = useSelector((state) => state.userInfo.id);
  const dispatch = useDispatch();

  const httpService = useAxios(axiosConfig.envURL);

  // Ref to track if the initial fetch has been performed
  const initialFetchRef = useRef(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      const response = await httpService.get(`/notifications/list/${userId}`);
      dispatch(setNotifications(response.data.data));
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      const items = notifications.map(notification => ({
        accountId: notification.accountId,
        createdDate: notification.createdDate
      }));
      // V2 expects { body: [...] }; V1 expects array (if still on V1, backend should accept both)
      await httpService.put(`/notifications/read/${userId}`, { body: items });
      

      dispatch(markAllAsRead());
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const markNotificationAsRead = async (notification) => {
    try {
      if (!notification.readStatus) {
        const items = [{
          accountId: notification.accountId,
          createdDate: notification.createdDate
        }];
        // V2 expects { body: [...] }
        await httpService.put(`/notifications/read/${userId}`, { body: items });
        fetchNotifications();
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  useEffect(() => {
    if (!initialFetchRef.current) {
      fetchNotifications();
      initialFetchRef.current = true;
    }
  });

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);

    if (!showNotifications) {
      fetchNotifications();
    }
  };

  const toggleShowUnread = () => {
    setShowUnread(!showUnread);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          !event.target.closest('.bell-icon-container')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const filteredNotifications = (showUnread
    ? notifications.filter(notification => !notification.readStatus)
    : notifications.filter(notification => notification.readStatus)
  ).sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));

  const formatNotificationText = (text, accountName) => {
    if (!accountName) return text;
    
    // Simply wrap the accountName in strong tags wherever it appears
    return text.replace(accountName, `<strong>${accountName}</strong>`);
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const createdDate = new Date(date);
    const diffInMs = now - createdDate;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    
    if (diffInHours < 24) {
      if (diffInHours < 1) {
        return `${diffInMinutes} ${diffInMinutes === 1 ? 'min' : 'mins'} ago`;
      }
      return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    }
  };

  const [showAllNotifications, setShowAllNotifications] = useState(false);

  const handleViewAll = () => {
    setShowAllNotifications(true);
    setShowNotifications(false); // Close the dropdown when opening full view
  };

  const handleCloseAllNotifications = () => {
    setShowAllNotifications(false);
  };

  const getIcon = (notification) => {
    if (notification.notificationContent.includes ("threshold")) {
      return bloodPressure;
    } else if (notification.notificationContent.includes ("report")) {
      return notes;
    } else {
      return device;
    }
  }

  const getSource = (notification) => {
    if (notification.notificationContent.includes ("threshold")) {
      return 'Perin Patch';
    } else if (notification.notificationContent.includes ("report")) {
      return 'Mobile';
    } else if (notification.notificationContent.includes ("Reminder")) {
      return 'System';
    } else {
      return 'Perin Patch';
    }
  };

  return (
    <div className="bell-notification">
      <div className="bell-icon-container" onClick={toggleNotifications}>
        {hasUnread ? <NotificationIconUnread /> : <NotificationIcon />}
      </div>
      {showNotifications && (
        <div className="notifications-dropdown" ref={dropdownRef}>
          <div style={{display:"flex"}}><div style={{flex: 1}}></div><CloseButton onClick={()=>setShowNotifications(false)} /></div>
          <div className="notifications-header">
            <div className="notifications-title">Notifications</div>
            {notifications.filter(notification => !notification.readStatus).length > 0 &&
              <div className="mark-all-as-read" onClick={markAllNotificationsAsRead}>Mark all as read</div>
            }
          </div>
          <div className="toggle-container">
            <label className="switch">
              <input type="checkbox" checked={showUnread} onChange={toggleShowUnread} />
              <span className="slider round"></span>
            </label>
            <span style={{
              fontFamily: "Roboto",
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: "24px",
              letterSpacing: "0.0025em",
              textAlign: "left",
              color: "#000000"
            }}>Show unread notifications</span>
          </div>
          <div className="notifications-list">
            {filteredNotifications.length > 0 ? (
              filteredNotifications.map((notification, index) => (
                <div 
                  key={index} 
                  className={`notification-item${notification.readStatus ? "" : '-unread'}`}
                  onClick={() => markNotificationAsRead(notification)}
                >
                  <div className="notification-circle">
                    <img 
                        src={getIcon(notification)} 
                        alt="bell notification" 
                        style={{
                            width: '24px',
                            height: '24px'
                          }}
                    />
                  </div>
                  <div className="notification-content">
                    <div dangerouslySetInnerHTML={{ 
                      __html: formatNotificationText(
                        notification.notificationContent, 
                        notification.accountName
                      ) 
                    }} />
                    <div className="notification-time">
                      <span>{`Priority: Low | Source: ${getSource(notification)}`}</span>
                      {`${formatTimeAgo(notification.createdDate)}`}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-notifications">
                <img src={image} alt="No notifications" />
                <div>No notification yet!</div>
                <div>Check back later for updates.</div>
              </div>
            )}
          </div>
          <div className="view-all-container">
            <div className="view-all-button" onClick={handleViewAll}>View All</div>
          </div>
        </div>
      )}
      {showAllNotifications && (
        <AllNotificationsPopup
          notifications={notifications}
          onClose={handleCloseAllNotifications}
          formatNotificationText={formatNotificationText}
          formatTimeAgo={formatTimeAgo}
          markNotificationAsRead={markNotificationAsRead}
        />
      )}
    </div>
  );
};

export default BellNotification;
